import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  Target,
  TrendingUp,
  DollarSign,
  Users,
  BarChart3,
  Search,
  Filter,
  Download,
  Activity
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { campaignMasterService } from '../../services/campaignMasterService'
import { formatRupeeAmount, formatINR } from '../../utils/currencyFormat';

const CampaignMaster = () => {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    category: ''
  })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  })

  // Define columns for the data table - Essential columns only
  const columns = [
    {
      key: 'campaignCode',
      label: 'Code',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          <Target size={14} className="text-blue-500" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'campaignName',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">{value}</div>
      )
    },
    {
      key: 'campaignType',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const typeColors = {
          'Email': 'bg-blue-100 text-blue-800',
          'Social Media': 'bg-purple-100 text-purple-800',
          'Digital': 'bg-green-100 text-green-800',
          'Traditional': 'bg-orange-100 text-orange-800',
          'Content': 'bg-pink-100 text-pink-800',
          'Event': 'bg-indigo-100 text-indigo-800',
          'Webinar': 'bg-cyan-100 text-cyan-800',
          'Retargeting': 'bg-red-100 text-red-800',
          'Influencer': 'bg-yellow-100 text-yellow-800',
          'Partnership': 'bg-teal-100 text-teal-800',
          'Other': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'campaignStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'Draft': { color: 'bg-gray-100 text-gray-800', icon: Clock },
          'Active': { color: 'bg-green-100 text-green-800', icon: Play },
          'Paused': { color: 'bg-yellow-100 text-yellow-800', icon: Pause },
          'Completed': { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
          'Cancelled': { color: 'bg-red-100 text-red-800', icon: AlertCircle }
        }
        const config = statusConfig[value] || statusConfig['Draft']
        const IconComponent = config.icon
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <IconComponent size={12} className="mr-1" />
            {value}
          </span>
        )
      }
    },
    {
      key: 'budget',
      label: 'Budget',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <DollarSign size={14} className="text-gray-400" />
          <span className="font-medium">{value ? formatRupeeAmount(value) : formatRupeeAmount(0)}</span>
        </div>
      )
    },
    {
      key: 'totalLeads',
      label: 'Leads',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <Users size={14} className="text-gray-400" />
          <span className="font-medium">{value || 0}</span>
        </div>
      )
    },
    {
      key: 'returnOnInvestment',
      label: 'ROI',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <TrendingUp size={14} className={value > 0 ? "text-green-500" : "text-red-500"} />
          <span className={`font-medium ${value > 0 ? "text-green-600" : "text-red-600"}`}>
            {value ? `${value.toFixed(1)}%` : '0%'}
          </span>
        </div>
      )
    }
  ];

  const loadData = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: searchTerm,
        ...filters
      }
      
      const response = await campaignMasterService.getAllCampaigns(params)
      
      if (response.success) {
        setCampaigns(response.data.campaigns)
        setPagination(response.data.pagination)
      } else {
        toast.error(response.message || 'Failed to load campaigns')
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load campaigns')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await campaignMasterService.getCampaignStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  useEffect(() => {
    loadData()
    loadStats()
  }, [pagination.currentPage, searchTerm, filters])

  const handleAdd = () => {
    navigate('/crm/campaign-master/new')
  }

  const handleEdit = (campaign) => {
    navigate(`/crm/campaign-master/edit/${campaign._id}`)
  }

  const handleView = (campaign) => {
    navigate(`/crm/campaign-master/view/${campaign._id}`)
  }

  const handleDelete = (campaign) => {
    setDeleteItem(campaign)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return
    
    try {
      const response = await campaignMasterService.deleteCampaign(deleteItem._id)
      if (response.success) {
        toast.success('Campaign deleted successfully')
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete campaign')
      }
    } catch (error) {
      console.error('Error deleting campaign:', error)
      toast.error('Failed to delete campaign')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return
    
    try {
      const response = await campaignMasterService.bulkDeleteCampaigns(selectedRows)
      if (response.success) {
        toast.success(`${selectedRows.length} campaigns deleted successfully`)
        setSelectedRows([])
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete campaigns')
      }
    } catch (error) {
      console.error('Error bulk deleting campaigns:', error)
      toast.error('Failed to delete campaigns')
    }
  }

  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Campaign Master',
      path: '/crm/campaign-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Campaign Master</h1>
          <p>Manage marketing campaigns and track performance metrics</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Add Campaign
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{stats.totalCampaigns || 0}</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCampaigns || 0}</div>
          <div className="metric-title">Total Campaigns</div>
          <div className="metric-subtitle">All campaigns</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{stats.activeCampaigns || 0}</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeCampaigns || 0}</div>
          <div className="metric-title">Active Campaigns</div>
          <div className="metric-subtitle">Currently running</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+₹{stats.totalBudget || 0}</span>
            </div>
          </div>
          <div className="metric-value">₹{stats.totalBudget || 0}</div>
          <div className="metric-title">Total Budget</div>
          <div className="metric-subtitle">Allocated budget</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{stats.avgROI || 0}%</span>
            </div>
          </div>
          <div className="metric-value">{stats.avgROI || 0}%</div>
          <div className="metric-title">Average ROI</div>
          <div className="metric-subtitle">Return on investment</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-container">
        <DataTable
          data={campaigns}
          columns={columns}
          loading={loading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          pagination={pagination}
          onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
          noCard={true}
          searchPlaceholder="Search campaigns..."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Campaign"
        message={`Are you sure you want to delete "${deleteItem?.campaignName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default CampaignMaster;
